import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { ReplaySubject, Subject, throttleTime } from 'rxjs';

import { BUILD_CONFIG } from '../static/build-config.token';
import { fromWindowEvent } from 'core/utils/rxjs/window-event';
import { WindowService } from './window.service';
import { USER_AGENT } from '../static/user-agent.token';

export interface Breakpoints {
  small: string; //0-768
  medium: string; //768-1024
  large: string; //1024-1440
  xlarge: string; //1300-1440
  xxlarge: string; //1440px+
}

export type MediaName = keyof Breakpoints;
type MediaNames = Array<keyof Breakpoints>;
type Queries = {
  name: keyof Breakpoints;
  value: string;
}[];

@Injectable({ providedIn: 'root' })
export class MediaQuery {
  queries: Queries = [];
  current: MediaName = 'small';
  breakpoints: MediaNames = ['small', 'medium', 'large', 'xlarge', 'xxlarge'];
  scaleRatio = 1;

  private mediaSubj = new ReplaySubject<{ new: MediaName; previous: MediaName }>(1);
  mediaChanged = this.mediaSubj.asObservable();
  private resizeSubj = new Subject<MediaName>();
  resized = this.resizeSubj.asObservable();
  private scaleChangedSubj = new Subject<number>();
  scaleChanged = this.scaleChangedSubj.asObservable();

  private matchMediaCache = new Map<string, boolean>();
  private baseFontsize: number = 16;

  constructor(
    private window: WindowService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(BUILD_CONFIG) private build: BaseBuildConfig,
    @Optional() @Inject(USER_AGENT) private uaFromServer?: string,
  ) {
    this.resizeSubj.pipe(throttleTime(150, undefined, { trailing: true })).subscribe(() => {
      // this.updateBaseFontsize();
      this.measureScaleRatio();
    });
    // this.updateBaseFontsize();
    this.measureScaleRatio();
  }

  private updateBaseFontsize() {
    let baseFontsize = this.baseFontsize;
    if (this.window.innerWidth > 1920) {
      baseFontsize = this.baseFontsize + ((Math.max(1440, this.window.innerWidth) - 1440) / 160) * 1.8;
    }
    this.document.documentElement.style.setProperty('--body-font-size', `${baseFontsize}px`);
  }

  private measureScaleRatio(): void {
    const initialRatio = this.scaleRatio;
    if (this.build.ssr) {
      this.scaleRatio = 1;
    } else {
      const el = this.document.createElement('div');
      el.classList.add('scale-reference');
      this.document.body.appendChild(el);
      this.scaleRatio = this.window.getBoundingClientRect(el).width / 100;
      el.remove();
    }
    if (this.scaleRatio !== initialRatio) {
      this.scaleChangedSubj.next(this.scaleRatio);
    }
  }

  /**
   * Initializes the media query service
   * @function
   * @private
   */
  init(options: { breakpoints: Breakpoints; baseFontsize: number }): void {
    this.baseFontsize = options.baseFontsize;
    const toEm = (breakpointValue: string) => `${parseInt(breakpointValue, 10) / options.baseFontsize}em`;
    const emBreakpoints: Partial<Breakpoints> = {};
    (Object.keys(options.breakpoints) as MediaNames).forEach(key => {
      emBreakpoints[key] = toEm(options.breakpoints[key]);
    });
    for (const key in emBreakpoints) {
      // eslint-disable-next-line no-prototype-builtins
      if (emBreakpoints.hasOwnProperty(key)) {
        this.queries.push({
          name: key as MediaName,
          value: `only screen and (min-width: ${emBreakpoints[key as MediaName]})`,
        });
      }
    }
    this.current = this.getCurrentSize();
    this.observeResize();
    this.mediaSubj.next({ new: this.current, previous: this.current });
  }

  /**
   * Checks if the screen is at least as wide as a breakpoint.
   * @function
   * @param {String} size - Name of the breakpoint to check.
   * @returns {Boolean} `true` if the breakpoint matches, `false` if it's smaller.
   */
  atLeast(size: MediaName): boolean {
    const query = this.get(size);
    if (query) {
      return this.matchMedia(query);
    }
    return false;
  }

  private matchMedia(query: string): boolean {
    const cachedResult = this.matchMediaCache.get(query);
    if (cachedResult !== undefined) {
      return cachedResult;
    }
    const result = this.window.matchMedia(query);
    // When SSR: emulate matchMedia using UA-based width
    if (this.uaFromServer) {
      const emulatedWidth = this.getEmulatedWidth();
      const minWidthMatch = /min-width:\s*([0-9.]+)(px|em)/.exec(query);
      const width = minWidthMatch ? parseFloat(minWidthMatch[1]) : 0;
      const unit = minWidthMatch ? minWidthMatch[2] : 'px';
      const baseFont = this.baseFontsize || 16;
      const widthPx = unit === 'em' ? width * baseFont : width;
      const evalResult = emulatedWidth >= widthPx;
      this.matchMediaCache.set(query, evalResult);
      return evalResult;
    }
    this.matchMediaCache.set(query, result);
    return result;
  }

  /**
   * Checks if the screen matches to a breakpoint.
   * @function
   * @param {String} size - Name of the breakpoint to check, either 'medium only' or 'medium' or even 'medium down'.
   * Omitting 'only' falls back to using atLeast() method.
   * @returns {Boolean} `true` if the breakpoint matches, `false` if it does not.
   */
  is(size: string): boolean {
    const sizeParts = size.trim().split(' ');
    if (sizeParts.length > 1 && sizeParts[1] === 'only') {
      if (sizeParts[0] === this.getCurrentSize()) {
        return true;
      }
    } else if (sizeParts.length > 1 && sizeParts[1] === 'down') {
      const current = this.breakpoints.indexOf(this.getCurrentSize());
      const max = this.breakpoints.indexOf(sizeParts[0] as MediaName);
      return current <= max;
    } else {
      return this.atLeast(sizeParts[0] as MediaName);
    }
    return false;
  }

  /**
   * Gets the media query of a breakpoint.
   * @function
   * @param {String} size - Name of the breakpoint to get.
   * @returns {String|null} - The media query of the breakpoint, or `null` if the breakpoint doesn't exist.
   */
  private get(size: MediaName): string | null {
    // eslint-disable-next-line @typescript-eslint/no-for-in-array
    for (const i in this.queries) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.queries.hasOwnProperty(i)) {
        const query = this.queries[i];
        if (size === query.name) {
          return query.value;
        }
      }
    }

    return null;
  }

  /**
   * Gets the current breakpoint name by testing every breakpoint and returning the last one to match (the biggest one).
   * @function
   * @private
   * @returns {String} Name of the current breakpoint.
   */
  private getCurrentSize(): MediaName {
    let matched: unknown;
    for (const q of this.queries) {
      if (this.matchMedia(q.value)) {
        matched = q;
      }
    }
    if (typeof matched === 'object') {
      return (matched as { name: MediaName }).name;
    } else {
      return matched as MediaName;
    }
  }

  isSmall(): boolean {
    return this.is('small only');
  }

  private getEmulatedWidth(): number {
    const ua = (this.uaFromServer || '').toLowerCase();
    if (!ua) return 1420;
    const isMobile = /iphone|android.*mobile|mobile|phone/i.test(ua);
    const isTablet = /ipad|tablet|silk|kindle|playbook|android(?!.*mobile)/i.test(ua);
    if (isMobile) return 475;
    if (isTablet) return 1024;
    return 1420;
  }

  /**
   * Activates the breakpoint watcher, which fires an event on the breakpoint changes.
   * @function
   * @private
   */
  private observeResize(): void {
    fromWindowEvent('resize').subscribe(() => {
      this.matchMediaCache = new Map();
      const newSize = this.getCurrentSize();
      if (newSize !== this.current) {
        // Change the current media query
        const previous = this.current;
        this.current = newSize;
        // Broadcast the media query change
        this.mediaSubj.next({ new: newSize, previous });
      }
      this.resizeSubj.next(this.current);
    });
  }
}
